/**
 * Firebase Cloud Messaging Service
 * Handles FCM token registration and push notifications for web
 */

import { getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';
import { messaging } from '@/firebase/config';
import { auth } from '@/firebase/config';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';

// VAPID Key for web push notifications
// Get this from Firebase Console: Project Settings > Cloud Messaging > Web configuration > Web Push certificates
// Paste your VAPID key here (hardcoded to avoid env file issues)
const VAPID_KEY = 'BAx8U-VlQXIBjU8Vdec1YiuAtwgObFHds145xyWUBVJjcvE6Z2cH9o64zWJpnpVzigkv_VPi4pV8kQXkI_wv5zw';

/**
 * Request notification permission (must be called from user action handler)
 * This function can be called from a click handler to request permission
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('⚠️ This browser does not support notifications');
    return 'denied';
  }

  const currentPermission = Notification.permission;
  if (currentPermission !== 'default') {
    return currentPermission;
  }

  try {
    // This must be called directly from a user action (click handler)
    const permission = await Notification.requestPermission();
    console.log('📱 Notification permission result:', permission);
    return permission;
  } catch (error) {
    console.warn('⚠️ Error requesting notification permission:', error);
    return Notification.permission;
  }
};

/**
 * Request notification permission and get FCM token
 * Note: Permission request must be triggered from a user action (click handler)
 */
export const requestFCMToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Check if messaging is supported
    const supported = await isSupported();
    if (!supported) {
      console.warn('FCM is not supported in this browser');
      return null;
    }

    if (!messaging) {
      console.warn('Firebase Messaging is not initialized');
      return null;
    }

    if (!VAPID_KEY) {
      console.warn('⚠️ VAPID key not configured. Please add VITE_FIREBASE_VAPID_KEY to .env file');
      console.warn('Get the VAPID key from Firebase Console: Project Settings > Cloud Messaging > Web Push certificates');
      return null;
    }

    // Check current permission status
    if (!('Notification' in window)) {
      console.warn('⚠️ This browser does not support notifications');
      return null;
    }

    const currentPermission = Notification.permission;
    console.log('📱 Current notification permission:', currentPermission);

    // Only proceed if permission is already granted
    // If permission is "default", we can't request it here (must be from user action)
    // If permission is "denied", user must enable in browser settings
    if (currentPermission !== 'granted') {
      if (currentPermission === 'default') {
        console.warn('⚠️ Notification permission not yet granted.');
        console.warn('💡 Permission must be requested from a user action (click handler).');
        console.warn('💡 Call requestNotificationPermission() from a button click to enable notifications.');
        return null;
      } else {
        // Permission was denied - browser won't show prompt again
        // User must manually enable in browser settings
        console.warn('❌ Notification permission was denied.');
        console.warn('📱 To enable notifications:');
        console.warn('   1. Click the lock/padlock icon in your browser address bar');
        console.warn('   2. Find "Notifications" in the permissions list');
        console.warn('   3. Change from "Block" to "Allow"');
        console.warn('   4. Refresh this page');
        console.warn('   Or go to: Browser Settings → Privacy → Site Settings → Notifications → Allow');
        return null;
      }
    }

    console.log('✅ Notification permission already granted');

    // Register service worker if not already registered
    let serviceWorkerRegistration;
    try {
      // Check if service worker is already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (existingRegistration) {
        serviceWorkerRegistration = existingRegistration;
      } else {
        serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service Worker registered for FCM');
      }
    } catch (error) {
      console.error('Error registering service worker:', error);
      // Try to get existing registration
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      if (existingRegistration) {
        serviceWorkerRegistration = existingRegistration;
      } else {
        throw error;
      }
    }

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration
    });

    if (token) {
      console.log('FCM token obtained:', token.substring(0, 20) + '...');
      return token;
    } else {
      console.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Register FCM token with user's document in Firestore
 * Now uses Cloud Function to register with platform metadata
 * OPTIMIZED: Only registers if token changed (reduces Cloud Function invocations)
 */
export const registerFCMToken = async (userId: string, token: string): Promise<boolean> => {
  try {
    if (!db) {
      console.error('Firestore not initialized');
      return false;
    }

    // Check if this token was already registered (avoid unnecessary Cloud Function calls)
    const storageKey = `fcm_token_${userId}`;
    const registeredToken = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
    if (registeredToken === token) {
      console.log('✅ FCM: Token already registered, skipping Cloud Function call');
      return true; // Token already registered, no need to call function
    }

    // Get Firebase Auth for API call
    const currentUser = auth?.currentUser;
    
    if (!currentUser) {
      console.error('User not authenticated');
      return false;
    }

    // Get Firebase Auth token
    const authToken = await currentUser.getIdToken();

    // Register with Cloud Function (includes platform metadata)
    const response = await fetch(
      'https://us-central1-lemur-476302.cloudfunctions.net/registerFCMToken',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fcmToken: token,
          platform: 'web'
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      console.error('Failed to register FCM token:', error);
      return false;
    }

    const result = await response.json();
    console.log('FCM token registered successfully:', result);
    
    // Cache the registered token locally
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, token);
    }
    
    return true;
  } catch (error) {
    console.error('Error registering FCM token:', error);
    return false;
  }
};

/**
 * Unregister FCM token from user's document
 */
export const unregisterFCMToken = async (userId: string, token: string): Promise<boolean> => {
  try {
    if (!db) return false;

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      fcmTokens: arrayRemove(token)
    });

    console.log('FCM token unregistered successfully');
    return true;
  } catch (error) {
    console.error('Error unregistering FCM token:', error);
    return false;
  }
};

/**
 * Request notification permission and initialize FCM
 * This function MUST be called from a user action handler (e.g., button click)
 * Use this when permission is "default" and needs to be requested
 */
export const requestPermissionAndInitializeFCM = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  const user = auth?.currentUser;
  if (!user) {
    console.log('⚠️ FCM: User not authenticated');
    return false;
  }

  try {
    // Request permission (must be from user action)
    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
      console.warn('⚠️ FCM: Notification permission not granted');
      return false;
    }

    // Now get FCM token and register
    const token = await requestFCMToken();
    if (token) {
      console.log('✅ FCM: Token obtained, registering with Firestore...');
      const registered = await registerFCMToken(user.uid, token);
      if (registered) {
        console.log('✅ FCM: Token registered successfully');
        return true;
      } else {
        console.warn('⚠️ FCM: Failed to register token');
        return false;
      }
    } else {
      console.warn('⚠️ FCM: No token obtained');
      return false;
    }
  } catch (error) {
    console.error('❌ FCM: Error requesting permission and initializing FCM:', error);
    return false;
  }
};

/**
 * Initialize FCM and register token for current user
 * Only works if permission is already granted
 * OPTIMIZED: Checks if token already registered before calling Cloud Function
 */
export const initializeFCM = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    console.log('⚠️ FCM: Not in browser environment, skipping');
    return;
  }

  const user = auth?.currentUser;
  if (!user) {
    console.log('⚠️ FCM: User not authenticated, skipping FCM initialization');
    return;
  }

  console.log('🚀 FCM: Initializing FCM for user:', user.uid);

  try {
    const token = await requestFCMToken();
    if (token) {
      // Check if token already registered (avoid unnecessary Cloud Function calls)
      const storageKey = `fcm_token_${user.uid}`;
      const registeredToken = localStorage.getItem(storageKey);
      if (registeredToken === token) {
        console.log('✅ FCM: Token already registered, skipping registration');
        return; // Token already registered, no need to call function
      }
      
      console.log('✅ FCM: Token obtained, registering with Firestore...');
      const registered = await registerFCMToken(user.uid, token);
      if (registered) {
        console.log('✅ FCM: Token registered successfully');
      } else {
        console.warn('⚠️ FCM: Failed to register token');
      }
    } else {
      // Permission not granted - this is expected if permission is "default"
      // User needs to call requestPermissionAndInitializeFCM() from a click handler
      console.log('ℹ️ FCM: Permission not granted yet. User can enable notifications by clicking a button.');
    }
  } catch (error) {
    console.error('❌ FCM: Error initializing FCM:', error);
  }
};

/**
 * Set up listener for foreground messages
 */
export const setupForegroundMessageListener = (onMessageReceived: (payload: any) => void) => {
  if (!messaging) {
    console.warn('Messaging not initialized');
    return;
  }

  onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    onMessageReceived(payload);
  });
};

